<script lang="ts" setup>
import { ref, Ref, computed, watch, inject, onBeforeMount, onBeforeUnmount } from 'vue';
import { useEvents } from '@/plugins/events';
import properties from '@/components/forms/properties';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { instanceOfBooleanEntry, BooleanType, BooleanEntry, BooleanFieldTypes } from '.';
import { useLocalization } from '@/plugins/localization';
import { Option } from '@/helpers/Interfaces';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger, useDynamicTriggerChannelId } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';

defineOptions({
    name: 'boolean-blueprint',
    components: {
        ...properties
    }
});

const { $t } = useLocalization();

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const getDynamicTriggerChannelId = useDynamicTriggerChannelId(() => props.form.blueprint.id);

const level = inject<number>('level', 0);
const { $events } = useEvents();
const blueprint = computed(() => props.blueprint);
const entryData = ref(new BooleanEntry()) as Ref<BooleanEntry>;

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfBooleanEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();
    }
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        return value === null
            ? props.blueprint.defaultValue
            : value;
    },
    set(value: boolean)
    {
        entryData.value.data = value;
        useDynamicTrigger($events, getDynamicTriggerChannelId()).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    }
});

const defaultValue = computed({
    get()
    {
        return props.blueprint.defaultValue;
    },
    set(value: any)
    {
        blueprint.value.defaultValue = JSON.parse(value);
    }
});

const trueValueText = computed<string>(() => props.form.localization.translate(props.blueprint.trueValueText) || $t('[[[Tak]]]'));
const falseValueText = computed<string>(() => props.form.localization.translate(props.blueprint.falseValueText) || $t('[[[Nie]]]'));
const placeholderText = computed<string>(() => props.form.localization.translate(props.blueprint.placeholder) || $t('[[[Akceptacja]]]'));

const booleanValueOptions = computed<Option<boolean>[]>(() => ([
    { value: true, text: trueValueText.value },
    { value: false, text: falseValueText.value }
]));

const fieldTypeOptions: Option<BooleanFieldTypes>[] = [
    { value: BooleanFieldTypes.Radio, text: '[[[Radio]]]' },
    { value: BooleanFieldTypes.Checkbox, text: '[[[Checkbox]]]' },
    { value: BooleanFieldTypes.Toggle, text: '[[[Przełącznik]]]' }
];

const isRadioField = computed(() => props.blueprint.fieldType === BooleanFieldTypes.Radio);
const isCheckboxField = computed(() => props.blueprint.fieldType === BooleanFieldTypes.Checkbox);
const isToggleField = computed(() => props.blueprint.fieldType === BooleanFieldTypes.Toggle);

const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));

watch(defaultValue, (value: boolean, old: boolean) =>
{
    if (value != old)
    {
        entryData.value.data = defaultValue.value;
    }
});
</script>

<template>
    <form-component-wrapper class="boolean-component" :form="form" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div class="pt-1 ms-2">
                    <ideo-form-radio-group v-model="value" :options="booleanValueOptions" v-if="isRadioField" :disabled="readonly"></ideo-form-radio-group>
                    <ideo-form-checkbox v-model="value" v-if="isCheckboxField" :disabled="readonly">{{ placeholderText }}</ideo-form-checkbox>
                    <ideo-form-checkbox v-model="value" switch v-if="isToggleField" :disabled="readonly">{{ placeholderText }}</ideo-form-checkbox>
                </div>
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-choice v-model="blueprint.fieldType" :options="fieldTypeOptions" :label="$t('[[[Typ pola]]]')" />
                    <field-select v-model="defaultValue" :options="booleanValueOptions" :label="$t('[[[Domyślna wartość]]]')" />
                    <ideo-form-localize v-if="!isRadioField" v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Opis]]]')" :placeholder="$t('[[[Akceptacja]]]')" />
                    </ideo-form-localize>
                    <ideo-form-localize v-if="isRadioField" v-slot="{ locale }">
                        <div class="form-group mb-0" v-if="isRadioField">
                            <label>{{ $t('[[[Nazwy wartości]]]') }}</label>
                            <div class="row">
                                <div class="col">
                                    <field-text v-model="blueprint.falseValueText[locale]" :placeholder="$t('[[[Nie]]]')" />
                                </div>
                                <div class="col">
                                    <field-text v-model="blueprint.trueValueText[locale]" :placeholder="$t('[[[Tak]]]')" />
                                </div>
                            </div>
                        </div>
                    </ideo-form-localize>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" />
                    <field-error :form="form" :blueprint="blueprint" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>

<style lang="scss" scoped>
</style>
